import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Chip, Checkbox, FormControlLabel, TextField, Divider, Alert } from '@mui/material';
import { MapPin, Ruler, Calendar, ShoppingCart, ArrowLeft, Eye, Building, CheckCircle, Trash2, Upload, Printer, Wrench, RotateCcw, ArrowRight, Package, IndianRupee } from 'lucide-react';
import { AdSpace, Order, Campaign } from '../../types';
import { spacesAPI, ordersAPI, campaignsAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { Card, PageHeader, SectionTitle, StatusBadge, EmptyState } from '../../components/common/UIComponents';

const inputSx = {
  '& .MuiOutlinedInput-root':{ background:'var(--bg-muted)', borderRadius:'10px !important', fontSize:13, fontFamily:'DM Sans,sans-serif', '& fieldset':{ borderColor:'var(--border)' }, '&:hover fieldset':{ borderColor:'#d4c4b0' }, '&.Mui-focused fieldset':{ borderColor:'var(--brand)' } },
  '& .MuiInputLabel-root':{ fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused':{ color:'var(--brand)' },
};

// ── Space Detail ───────────────────────────────────────────────────────────────
export const SpaceDetail: React.FC = () => {
  const { id } = useParams<{ id:string }>();
  const navigate = useNavigate();
  const [space, setSpace] = useState<AdSpace|null>(null);
  const [activeImg, setActiveImg] = useState(0);
  const { addToCart, items } = useCart();

  useEffect(() => { spacesAPI.getById(id!).then(setSpace); }, [id]);
  if (!space) return <div style={{ textAlign:'center', padding:80, color:'var(--text-muted)', fontFamily:'DM Sans' }}>Loading...</div>;

  const inCart = items.some(i => i.space.id === space.id);
  const baseAmount = space.rentAmount + space.commission;
  const gstAmount = Math.round(baseAmount * space.gst / 100);
  const total = baseAmount + gstAmount;

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <button onClick={()=>navigate(-1)} style={{ background:'none', border:'none', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'DM Sans', fontSize:13, display:'flex', alignItems:'center', gap:6, marginBottom:20, padding:0, fontWeight:500 }}>
        <ArrowLeft size={15}/> Back to Search
      </button>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <div style={{ borderRadius:18, overflow:'hidden', marginBottom:10, height:390, boxShadow:'var(--shadow-md)' }}>
            <img src={space.images[activeImg]} alt={space.title} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          </div>
          {space.images.length > 1 && (
            <div style={{ display:'flex', gap:8 }}>
              {space.images.map((img,i) => (
                <div key={i} onClick={()=>setActiveImg(i)} style={{ width:80, height:58, borderRadius:10, overflow:'hidden', border:`2.5px solid ${i===activeImg?'var(--brand)':'transparent'}`, cursor:'pointer', opacity:i===activeImg?1:0.7, transition:'all 0.18s' }}>
                  <img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
              ))}
            </div>
          )}
          <Card style={{ marginTop:18 }}>
            <SectionTitle>About this Space</SectionTitle>
            <p style={{ color:'var(--text-secondary)', fontSize:14, lineHeight:1.75, margin:0 }}>{space.description}</p>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card style={{ position:'sticky', top:80 }}>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginBottom:14 }}>
              <span style={{ background:'var(--brand-light)', color:'var(--brand)', border:'1px solid var(--brand-mid)', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, fontFamily:'DM Sans' }}>{space.mediaType}</span>
              {space.isFeatured && <span style={{ background:'#fef6e4', color:'#92610a', border:'1px solid #fde8a8', padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600, fontFamily:'DM Sans' }}>Featured</span>}
              <span style={{ background:'var(--bg-muted)', color:'var(--text-secondary)', padding:'3px 10px', borderRadius:20, fontSize:12, fontFamily:'DM Sans' }}>{space.propertyType==='Government Contractor'?'Govt.':'Private'}</span>
            </div>
            <h1 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:21, margin:'0 0 10px', lineHeight:1.3, letterSpacing:'-0.02em' }}>{space.title}</h1>
            <div style={{ display:'flex', alignItems:'center', gap:5, color:'var(--text-muted)', fontSize:13, marginBottom:20 }}>
              <MapPin size={13} color="var(--brand)"/> {space.address}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
              {[{ icon:<Ruler size={13}/>, label:'Dimensions', value:space.size }, { icon:<Eye size={13}/>, label:'Traffic Priority', value:space.trafficPriority }, { icon:<Building size={13}/>, label:'Available From', value:new Date(space.availableFrom).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) }, { icon:<Calendar size={13}/>, label:'State', value:space.state }].map((d,i) => (
                <div key={i} style={{ background:'var(--bg-muted)', borderRadius:10, padding:'10px 12px' }}>
                  <div style={{ color:'var(--text-muted)', fontSize:11, display:'flex', alignItems:'center', gap:4, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' }}>{d.icon} {d.label}</div>
                  <div style={{ color:'var(--text-primary)', fontWeight:600, fontSize:13 }}>{d.value}</div>
                </div>
              ))}
            </div>
            <div style={{ background:'var(--bg-muted)', borderRadius:13, padding:'16px', marginBottom:20, border:'1px solid var(--border)' }}>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--text-primary)', marginBottom:12 }}>Price Breakdown</div>
              {[{ label:'Provider Rent', val:space.rentAmount }, { label:`GST (${space.gst}%)`, val:gstAmount }].map((r,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ color:'var(--text-secondary)', fontSize:13 }}>{r.label}</span>
                  <span style={{ color:'var(--text-primary)', fontSize:13, fontWeight:500 }}>₹{r.val.toLocaleString('en-IN')}</span>
                </div>
              ))}
              <Divider sx={{ my:1.2, borderColor:'var(--border)' }}/>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontWeight:700, fontSize:14, color:'var(--text-primary)' }}>Total Payable</span>
                <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:22 }}>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <button onClick={()=>{ if(!inCart) addToCart(space); navigate('/brand/cart'); }}
              className="btn-brand" style={{ width:'100%', padding:'13px', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:inCart?undefined:undefined }}>
              <ShoppingCart size={17}/>{inCart?'Go to Cart':'Add to Cart'}
            </button>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

// ── Cart ───────────────────────────────────────────────────────────────────────
const PRINT_FEE = 6000;
const INSTALL_FEE = 4500;

export const CartPage: React.FC = () => {
  const { items, removeFromCart, updateItem, clearCart, itemCount } = useCart();
  const [campaignName, setCampaignName] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const subtotal = items.reduce((s,i) => s + i.space.rentAmount + i.space.commission, 0);
  const printing = items.reduce((s,i) => s + (i.needsPrinting ? PRINT_FEE : 0), 0);
  const install  = items.reduce((s,i) => s + (i.needsInstallation ? INSTALL_FEE : 0), 0);
  const gst = Math.round((subtotal + printing + install) * 0.18);
  const grand = subtotal + printing + install + gst;

  const placeOrder = async () => {
    if (!campaignName.trim()) return;
    setLoading(true); await new Promise(r=>setTimeout(r,1200));
    setLoading(false); setDone(true); clearCart();
    setTimeout(()=>navigate('/brand/orders'), 2500);
  };

  if (done) return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', background:'#fff', borderRadius:24, padding:'48px 56px', boxShadow:'var(--shadow-lg)', border:'1px solid var(--border)' }}>
        <div style={{ width:72, height:72, borderRadius:'50%', background:'var(--success-bg)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:20, fontSize:34 }}>🎉</div>
        <h2 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:24, margin:'0 0 8px' }}>Order Submitted!</h2>
        <p style={{ color:'var(--text-muted)', fontSize:14, margin:0 }}>Your campaign has been submitted for admin review.</p>
      </div>
    </div>
  );

  if (itemCount === 0) return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center' }}>
        <EmptyState icon={<ShoppingCart size={28}/>} title="Your cart is empty" subtitle="Add advertising spaces to get started."
          action={<button onClick={()=>navigate('/brand/search')} className="btn-brand" style={{ padding:'11px 22px', fontSize:14 }}>Browse Spaces</button>}/>
      </div>
    </div>
  );

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Cart & Checkout" subtitle={`${itemCount} location${itemCount>1?'s':''} selected`}/>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {items.map(item => (
              <Card key={item.space.id} style={{ padding:'20px' }}>
                <div style={{ display:'flex', gap:14, marginBottom:14 }}>
                  <img src={item.space.images[0]} alt="" style={{ width:88, height:64, objectFit:'cover', borderRadius:10, flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', marginBottom:3 }}>{item.space.title}</div>
                        <div style={{ color:'var(--text-muted)', fontSize:12, display:'flex', alignItems:'center', gap:4 }}><MapPin size={11} color="var(--brand)"/>{item.space.city}, {item.space.state}</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:18 }}>₹{item.space.rentAmount.toLocaleString('en-IN')}</span>
                        <button onClick={()=>removeFromCart(item.space.id)} style={{ background:'var(--danger-bg)', border:'1px solid #f8c0c0', borderRadius:8, padding:'5px', color:'var(--danger)', cursor:'pointer', display:'flex' }}><Trash2 size={14}/></button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div style={{ background:'var(--bg-muted)', borderRadius:11, padding:'12px 16px', marginBottom:12, border:'1px solid var(--border-light)' }}>
                  <div style={{ color:'var(--text-muted)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>Optional Services</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                    {[
                      { key:'needsPrinting', label:`Print Banner (+₹${PRINT_FEE.toLocaleString('en-IN')})`, icon:<Printer size={12}/> },
                      { key:'needsInstallation', label:`Install (+₹${INSTALL_FEE.toLocaleString('en-IN')})`, icon:<Wrench size={12}/> },
                      { key:'needsRemoval', label:'Remove After Campaign', icon:<RotateCcw size={12}/> },
                    ].map(svc => (
                      <FormControlLabel key={svc.key}
                        control={<Checkbox checked={(item as any)[svc.key]} size="small" onChange={e=>{
                          const u:any = { [svc.key]:e.target.checked };
                          if(svc.key==='needsPrinting') u.printingCharge = e.target.checked?PRINT_FEE:0;
                          if(svc.key==='needsInstallation') u.installationCharge = e.target.checked?INSTALL_FEE:0;
                          updateItem(item.space.id, u);
                        }} sx={{ color:'var(--brand)','&.Mui-checked':{color:'var(--brand)'},padding:'4px' }}/>}
                        label={<span style={{ color:'var(--text-secondary)', fontSize:12, fontFamily:'DM Sans', display:'flex', alignItems:'center', gap:4 }}>{svc.icon}{svc.label}</span>}/>
                    ))}
                  </div>
                </div>

                <label style={{ display:'block', cursor:'pointer' }}>
                  <div style={{ border:'1.5px dashed var(--border)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8, color:'var(--text-muted)', fontSize:12, fontFamily:'DM Sans', transition:'all 0.18s' }}
                    onMouseEnter={e=>{(e.currentTarget).style.borderColor='var(--brand)';(e.currentTarget).style.color='var(--brand)';}}
                    onMouseLeave={e=>{(e.currentTarget).style.borderColor='var(--border)';(e.currentTarget).style.color='var(--text-muted)';}}>
                    <Upload size={14}/> Upload Creative File — JPG, PNG, PDF, AI, PSD, ZIP
                    {item.creativeFile && <span style={{ marginLeft:'auto', color:'var(--success)', fontSize:11 }}>✓ {item.creativeFile}</span>}
                  </div>
                  <input type="file" accept=".jpg,.jpeg,.png,.pdf,.ai,.psd,.zip" hidden onChange={e=>updateItem(item.space.id,{creativeFile:e.target.files?.[0]?.name})}/>
                </label>
                <TextField label="Installation Notes" multiline rows={2} fullWidth sx={{...inputSx,mt:1.5}} size="small" value={item.installationNotes||''} onChange={e=>updateItem(item.space.id,{installationNotes:e.target.value})} placeholder="Special instructions for installation team..."/>
              </Card>
            ))}
          </div>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card style={{ position:'sticky', top:80 }}>
            <SectionTitle>Order Summary</SectionTitle>
            <TextField label="Campaign Name *" value={campaignName} onChange={e=>setCampaignName(e.target.value)} fullWidth sx={{...inputSx,mb:2.5}} placeholder="e.g. Summer 2024 Campaign"/>
            <div style={{ display:'flex', flexDirection:'column', gap:9, marginBottom:16 }}>
              {[{ l:`Rent (${itemCount} loc.)`, v:subtotal }, { l:'Printing', v:printing }, { l:'Installation', v:install }, { l:'GST (18%)', v:gst }].map((r,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:'var(--text-secondary)', fontSize:13 }}>{r.l}</span>
                  <span style={{ color:r.v>0?'var(--text-primary)':'var(--text-muted)', fontSize:13, fontWeight:500 }}>₹{r.v.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
            <Divider sx={{ borderColor:'var(--border)', mb:2 }}/>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <span style={{ fontWeight:700, fontSize:15, color:'var(--text-primary)' }}>Grand Total</span>
              <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:24 }}>₹{grand.toLocaleString('en-IN')}</span>
            </div>
            {!campaignName.trim() && <Alert severity="info" sx={{ mb:2, borderRadius:'10px !important', fontSize:12 }}>Please enter a campaign name to proceed.</Alert>}
            <button onClick={placeOrder} disabled={loading||!campaignName.trim()} className="btn-brand" style={{ width:'100%', padding:'13px', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', gap:8, opacity:!campaignName.trim()?0.5:1, cursor:!campaignName.trim()?'not-allowed':'pointer' }}>
              {loading?'Submitting...':<><ArrowRight size={17}/>Submit Order</>}
            </button>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

// ── Orders List ────────────────────────────────────────────────────────────────
export const OrdersList: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const navigate = useNavigate();
  useEffect(()=>{ ordersAPI.getAll().then(setOrders); },[]);

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="My Orders" subtitle="Track and manage all your advertising orders"/>
      {orders.length === 0 ? <EmptyState icon={<Package size={28}/>} title="No orders yet" subtitle="Book your first ad space to get started."/> : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {orders.map(o => (
            <div key={o.id} onClick={()=>navigate(`/brand/orders/${o.id}`)}
              className="card-hover" style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'18px 22px', cursor:'pointer', boxShadow:'var(--shadow-sm)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:15, color:'var(--text-primary)' }}>{o.campaignName}</span>
                    <span style={{ color:'var(--text-muted)', fontSize:12 }}>{o.id}</span>
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:14, color:'var(--text-muted)', fontSize:12 }}>
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}><MapPin size={12} color="var(--brand)"/>{o.items.length} location{o.items.length>1?'s':''}</span>
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}><Calendar size={12}/>{new Date(o.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
                    <span style={{ display:'flex', alignItems:'center', gap:4, color:'var(--brand)', fontWeight:600 }}><IndianRupee size={12}/>₹{o.grandTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ background:o.paymentStatus==='Received'?'var(--success-bg)':'var(--warning-bg)', color:o.paymentStatus==='Received'?'var(--success)':'var(--warning)', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>{o.paymentStatus}</span>
                  <StatusBadge status={o.status} size="sm"/>
                  <ArrowRight size={15} color="var(--text-muted)"/>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Order Detail ───────────────────────────────────────────────────────────────
export const OrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const orderId = window.location.pathname.split('/').pop();
  const [order, setOrder] = useState<Order|null>(null);
  useEffect(()=>{ ordersAPI.getById(orderId!).then(setOrder); },[orderId]);
  if(!order) return null;

  const steps = ['Submitted','Payment Received','Admin Review','Approved','Field Staff Assigned','Installation Completed','Proof Uploaded','Campaign Closed'];
  const cur = steps.indexOf(order.status);

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <button onClick={()=>navigate(-1)} style={{ background:'none', border:'none', color:'var(--text-secondary)', cursor:'pointer', fontFamily:'DM Sans', fontSize:13, display:'flex', alignItems:'center', gap:6, marginBottom:20, padding:0 }}>
        <ArrowLeft size={15}/> Back to Orders
      </button>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:24 }}>
        <div>
          <h1 style={{ fontWeight:700, fontSize:24, color:'var(--text-primary)', margin:'0 0 4px', letterSpacing:'-0.02em' }}>{order.campaignName}</h1>
          <p style={{ color:'var(--text-muted)', fontSize:13, margin:0 }}>{order.id} · Placed {new Date(order.createdAt).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</p>
        </div>
        <StatusBadge status={order.status}/>
      </div>

      {/* Progress Stepper */}
      <Card style={{ marginBottom:20, overflowX:'auto' }}>
        <SectionTitle>Campaign Progress</SectionTitle>
        <div style={{ display:'flex', alignItems:'center', minWidth:660, paddingBottom:4 }}>
          {steps.map((step,i) => (
            <React.Fragment key={step}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:'0 0 auto' }}>
                <div style={{ width:30, height:30, borderRadius:'50%', background: i<cur?'var(--brand)':i===cur?'var(--brand)':'#fff', border:`2px solid ${i<=cur?'var(--brand)':'var(--border)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:i<=cur?'#fff':'var(--text-muted)', fontWeight:700, transition:'all 0.3s' }}>
                  {i<cur?'✓':i+1}
                </div>
                <div style={{ color:i<=cur?'var(--text-primary)':'var(--text-muted)', fontSize:10, textAlign:'center', marginTop:6, maxWidth:72, lineHeight:1.3, fontWeight:i===cur?600:400 }}>{step}</div>
              </div>
              {i<steps.length-1 && <div style={{ flex:1, height:2, background:i<cur?'var(--brand)':'var(--border)', margin:'0 3px', marginBottom:22, transition:'background 0.4s' }}/>}
            </React.Fragment>
          ))}
        </div>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <SectionTitle>Booked Locations ({order.items.length})</SectionTitle>
            {order.items.map((item,i) => (
              <div key={i} style={{ display:'flex', gap:12, padding:'13px 0', borderBottom:i<order.items.length-1?'1px solid var(--border-light)':'none' }}>
                <img src={item.space.images[0]} alt="" style={{ width:76, height:54, objectFit:'cover', borderRadius:10, flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', marginBottom:3 }}>{item.space.title}</div>
                  <div style={{ color:'var(--text-muted)', fontSize:12, display:'flex', alignItems:'center', gap:4 }}><MapPin size={11} color="var(--brand)"/>{item.space.city}, {item.space.state}</div>
                  <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
                    {item.needsPrinting && <span style={{ background:'var(--info-bg)', color:'var(--info)', padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:600 }}>Printing</span>}
                    {item.needsInstallation && <span style={{ background:'#f3e8fc', color:'#6a1ab8', padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:600 }}>Installation</span>}
                  </div>
                </div>
                <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:17, whiteSpace:'nowrap' }}>₹{item.space.rentAmount.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <SectionTitle>Payment Summary</SectionTitle>
            {[{ l:'Provider Rent', v:order.totalRent }, { l:'Printing', v:order.totalPrinting }, { l:'Installation', v:order.totalInstallation }, { l:'GST', v:order.totalGST }].map((r,i) => (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ color:'var(--text-secondary)', fontSize:13 }}>{r.l}</span>
                <span style={{ color:r.v>0?'var(--text-primary)':'var(--text-muted)', fontSize:13 }}>₹{r.v.toLocaleString('en-IN')}</span>
              </div>
            ))}
            <Divider sx={{ borderColor:'var(--border)', my:1.5 }}/>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:700, fontSize:14, color:'var(--text-primary)' }}>Grand Total</span>
              <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:22 }}>₹{order.grandTotal.toLocaleString('en-IN')}</span>
            </div>
            {order.fieldStaff && (
              <div style={{ marginTop:16, background:'var(--bg-muted)', borderRadius:10, padding:'11px 14px', border:'1px solid var(--border)' }}>
                <div style={{ color:'var(--text-muted)', fontSize:11, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>Field Staff Assigned</div>
                <div style={{ color:'var(--text-primary)', fontWeight:600, fontSize:13 }}>{order.fieldStaff}</div>
              </div>
            )}
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

// ── Campaigns ──────────────────────────────────────────────────────────────────
export const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const navigate = useNavigate();
  useEffect(()=>{ campaignsAPI.getAll().then(setCampaigns); },[]);

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="My Campaigns" subtitle="Monitor all your active and past campaigns"/>
      <Grid container spacing={2.5}>
        {campaigns.map(c => (
          <Grid item xs={12} md={6} xl={4} key={c.id}>
            <div className="card-hover" onClick={()=>navigate(`/brand/orders/${c.orderId}`)}
              style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:18, padding:'20px', cursor:'pointer', boxShadow:'var(--shadow-sm)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                <h3 style={{ fontWeight:700, fontSize:15, color:'var(--text-primary)', margin:0, flex:1, paddingRight:10, lineHeight:1.35 }}>{c.name}</h3>
                <StatusBadge status={c.status} size="sm"/>
              </div>
              {[{ l:'Locations', v:`${c.locations} site${c.locations>1?'s':''}` }, { l:'Budget', v:`₹${c.budget.toLocaleString('en-IN')}` }, { l:'Start', v:new Date(c.startDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) }, { l:'End', v:new Date(c.endDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) }].map((r,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:i<3?'1px solid var(--border-light)':'none' }}>
                  <span style={{ color:'var(--text-muted)', fontSize:12 }}>{r.l}</span>
                  <span style={{ color:'var(--text-primary)', fontSize:13, fontWeight:600 }}>{r.v}</span>
                </div>
              ))}
            </div>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

// ── Invoices ───────────────────────────────────────────────────────────────────
export const InvoicesPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  useEffect(()=>{ ordersAPI.getAll().then(o=>setOrders(o.filter(x=>x.paymentStatus==='Received'))); },[]);

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Invoices & Documents" subtitle="Download your invoices, receipts and campaign summaries"/>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {orders.map(o => (
          <div key={o.id} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'16px 22px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, boxShadow:'var(--shadow-sm)' }}>
            <div>
              <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', marginBottom:3 }}>{o.campaignName}</div>
              <div style={{ color:'var(--text-muted)', fontSize:12 }}>{o.id} · {new Date(o.createdAt).toLocaleDateString('en-IN')} · <span style={{ color:'var(--brand)', fontWeight:600 }}>₹{o.grandTotal.toLocaleString('en-IN')}</span></div>
            </div>
            <div style={{ display:'flex', gap:7 }}>
              {['Tax Invoice','Proforma','Receipt'].map(doc => (
                <button key={doc} className="btn-ghost" style={{ padding:'6px 13px', fontSize:12 }}>↓ {doc}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


